// A drafted lead email kept as a deal note, and the note left when it is sent. The note text is the record:
//   DRAFT EMAIL (not sent, 2026-10-25)
//   To: mona@acme.com            (optional, set by Personalise)
//   From: personal               (optional: personal | sales)
//   Attach: Brochure A + Brochure B
//   Subject: ...
//   <blank line>
//   body
// Personalise picks the best person on the deal, greets them by name and signs as the sender.
import {
  bestRecipient,
  personaliseBody,
  rankOf,
  senderOf,
  signatureFor,
  type Recipient,
} from "../../../../../supabase/functions/_shared/outreach.ts";
import type { Sender } from "./outlook";

export {
  bestRecipient,
  personaliseBody,
  rankOf,
  senderOf,
  signatureFor,
  type Recipient,
};

export const DRAFT_MARK = "DRAFT EMAIL";
export const SENT_MARK = "EMAIL SENT";

export type DraftNote = {
  subject: string;
  body: string;
  attach: string[];
  to?: string;
  from?: Sender;
};

/** The draft in a note, or null when the note is not one */
export const parseDraftNote = (text: string): DraftNote | null => {
  if (!text.startsWith(DRAFT_MARK)) return null;
  const lines = text.split("\n").slice(1);
  const attachLine = lines.find((l) => l.startsWith("Attach:"));
  const subjectAt = lines.findIndex((l) => l.startsWith("Subject:"));
  if (subjectAt < 0) return null;
  const header = (name: string) =>
    lines
      .slice(0, subjectAt)
      .find((l) => l.startsWith(`${name}:`))
      ?.slice(name.length + 1)
      .trim() || undefined;
  const from = header("From");
  return {
    ...(header("To") && { to: header("To") }),
    ...((from === "personal" || from === "sales") && { from }),
    subject: lines[subjectAt].slice("Subject:".length).trim(),
    body: lines
      .slice(subjectAt + 1)
      .join("\n")
      .trim(),
    attach: (attachLine?.slice("Attach:".length) ?? "")
      .split("+")
      .map((name) => name.trim())
      .filter((name) => name && name.toLowerCase() !== "none"),
  };
};

/** The note that records a send */
export const sentNote = (input: {
  to: string;
  from: string;
  subject: string;
  body: string;
  attached: string[];
  linked: string[];
}) =>
  [
    `${SENT_MARK} (${new Date().toISOString().slice(0, 16).replace("T", " ")})`,
    `To: ${input.to}`,
    `From: ${input.from}`,
    input.attached.length ? `Attached: ${input.attached.join(", ")}` : "",
    input.linked.length ? `Linked: ${input.linked.join(", ")}` : "",
    `Subject: ${input.subject}`,
    "",
    input.body,
  ]
    .filter((line, i, all) => line || i === all.length - 2)
    .join("\n");

/** The text of a draft note (the latest DRAFT EMAIL note on a deal is its current draft) */
export const draftNoteText = (draft: DraftNote) =>
  [
    `${DRAFT_MARK} (not sent, ${new Date().toISOString().slice(0, 10)})`,
    draft.to ? `To: ${draft.to}` : "",
    draft.from ? `From: ${draft.from}` : "",
    `Attach: ${draft.attach.join(" + ") || "none"}`,
    `Subject: ${draft.subject}`,
    "",
    draft.body,
  ]
    .filter((line, i, all) => line || i === all.length - 2)
    .join("\n");
